import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { dayKey, formatDaySeparator, formatTime } from '../utils/format';
import { EMOJI_CATEGORIES, REACTION_QUICK_EMOJIS, pushRecent } from './supportTelegramChrome';

export type SupportMsgRole = 'user' | 'support';

export type ReactionChip = {
  emoji: string;
  count: number;
  me?: boolean;
  user_ids?: number[];
};

export interface SupportMsgLike {
  id: number;
  author_role: SupportMsgRole;
  message_type?: string;
  body: string;
  created_at: string;
  edited_at?: string | null;
  author_telegram_id?: number;
  forwarded_from_message_id?: number | null;
}

export function isForwardedMessage(m: { forwarded_from_message_id?: number | null }): boolean {
  return m.forwarded_from_message_id != null && Number(m.forwarded_from_message_id) > 0;
}

/** Long-press for message menus — cancels on move, scroll, or release. */
export const MESSAGE_LONG_PRESS_MS = 650;
export const MESSAGE_LONG_PRESS_MOVE_PX = 18;

export function bindMessageLongPress(
  e: { pointerType: string; clientX: number; clientY: number },
  onFire: (x: number, y: number) => void,
  cancelSlot?: { current: (() => void) | null },
): void {
  if (e.pointerType === 'mouse') return;
  const startX = e.clientX;
  const startY = e.clientY;
  const timer = window.setTimeout(() => {
    cleanup();
    onFire(startX, startY);
  }, MESSAGE_LONG_PRESS_MS);
  const onMove = (ev: PointerEvent) => {
    if (Math.hypot(ev.clientX - startX, ev.clientY - startY) > MESSAGE_LONG_PRESS_MOVE_PX) {
      cleanup();
    }
  };
  const cleanup = () => {
    window.clearTimeout(timer);
    window.removeEventListener('pointerup', cleanup);
    window.removeEventListener('pointercancel', cleanup);
    window.removeEventListener('pointermove', onMove);
    if (cancelSlot?.current === cleanup) cancelSlot.current = null;
  };
  if (cancelSlot) cancelSlot.current = cleanup;
  window.addEventListener('pointerup', cleanup);
  window.addEventListener('pointercancel', cleanup);
  window.addEventListener('pointermove', onMove);
}

export function ClosedTicketBanner({
  variant = 'portal',
  onCreateTicket,
}: {
  variant?: 'portal' | 'admin';
  onCreateTicket?: () => void;
}) {
  const prefix = variant === 'admin' ? 'support-admin' : 'support';
  if (variant === 'portal') {
    return (
      <div className={`${prefix}-closed-banner`} role="status">
        <span className={`${prefix}-closed-banner-icon`} aria-hidden>
          ✓
        </span>
        <div className={`${prefix}-closed-banner-copy`}>
          <strong>Обращение закрыто</strong>
          <span>Если остались вопросы — создайте новое обращение.</span>
          {onCreateTicket ? (
            <button type="button" className={`${prefix}-closed-banner-cta`} onClick={onCreateTicket}>
              Создать новый тикет
            </button>
          ) : null}
        </div>
      </div>
    );
  }
  return (
    <div className={`${prefix}-closed-banner`} role="status">
      <span className={`${prefix}-closed-banner-icon`} aria-hidden>
        ✓
      </span>
      <div className={`${prefix}-closed-banner-copy`}>
        <strong>Тикет закрыт</strong>
        <span>Клиент больше не может писать сюда. Откройте тикет снова, чтобы продолжить переписку.</span>
      </div>
    </div>
  );
}

export function ForwardedLabel() {
  return <div className="support-msg-forwarded">Переслано</div>;
}

export function forwardSnippet(item: {
  message_type?: string | null;
  body?: string | null;
}): string {
  const t = item.message_type || 'text';
  if (t === 'image') return 'Фотография';
  if (t === 'video') return 'Видео';
  if (t === 'voice') return 'Голосовое сообщение';
  if (t === 'video_note') return 'Видеосообщение';
  if (t === 'sticker') return 'Стикер';
  if (t === 'gif') return 'GIF';
  if (t === 'album') return 'Альбом';
  const text = (item.body || '').trim().replace(/\s+/g, ' ');
  return text.slice(0, 90) || 'Сообщение';
}

export function forwardAuthorLabel(
  role: string | null | undefined,
  opts?: {
    username?: string | null;
    firstName?: string | null;
    selfIsUser?: boolean;
    supportDisplayName?: string | null;
  },
): string {
  if (role === 'support') {
    const alias = (opts?.supportDisplayName || '').trim();
    return alias || 'Поддержка';
  }
  if (opts?.selfIsUser) return 'Вы';
  const name = (opts?.username || opts?.firstName || '').trim();
  return name ? (opts?.username ? `@${opts.username.replace(/^@/, '')}` : name) : 'Клиент';
}

export function ForwardQuotePreview({
  author,
  snippet,
  thumbUrl,
  onOpen,
  variant = 'portal',
}: {
  author: string;
  snippet: string;
  thumbUrl?: string | null;
  onOpen?: () => void;
  variant?: 'portal' | 'admin';
}) {
  const prefix = variant === 'admin' ? 'support-admin' : 'support';
  return (
    <button
      type="button"
      className={`${prefix}-forward-quote`}
      onClick={(e) => {
        e.stopPropagation();
        onOpen?.();
      }}
    >
      <span className={`${prefix}-forward-quote-bar`} aria-hidden />
      <span className={`${prefix}-forward-quote-main`}>
        <span className={`${prefix}-forward-quote-author`}>{author}</span>
        <span className={`${prefix}-forward-quote-snippet`}>{snippet}</span>
      </span>
      {thumbUrl ? (
        <img src={thumbUrl} alt="" className={`${prefix}-forward-quote-thumb`} draggable={false} />
      ) : null}
    </button>
  );
}

export function buildMessageRows<T extends SupportMsgLike>(
  messages: T[],
): Array<{ type: 'day'; key: string; label: string } | { type: 'msg'; key: string; message: T }> {
  const rows: Array<
    { type: 'day'; key: string; label: string } | { type: 'msg'; key: string; message: T }
  > = [];
  let lastDay = '';
  for (const m of messages) {
    const dk = dayKey(m.created_at);
    if (dk !== lastDay) {
      lastDay = dk;
      rows.push({ type: 'day', key: `day-${dk}`, label: formatDaySeparator(m.created_at) });
    }
    rows.push({ type: 'msg', key: `m-${m.id}`, message: m });
  }
  return rows;
}

export function MessageTime({
  createdAt,
  editedAt,
  receipt,
  reactions,
  myTelegramId,
  onToggleReaction,
  reactionsDisabled,
}: {
  createdAt: string;
  editedAt?: string | null;
  receipt?: 'pending' | 'sent' | 'delivered' | 'read' | null;
  reactions?: ReactionChip[] | null;
  myTelegramId?: number | null;
  onToggleReaction?: (emoji: string) => void;
  reactionsDisabled?: boolean;
}) {
  const hasReactions = Boolean(reactions?.length);
  return (
    <div className={`support-msg-time${hasReactions ? ' has-reactions' : ''}`}>
      {hasReactions ? (
        <MessageReactionsBar
          reactions={reactions}
          myTelegramId={myTelegramId}
          onToggle={onToggleReaction || (() => undefined)}
          disabled={reactionsDisabled || !onToggleReaction}
        />
      ) : (
        <span className="support-msg-time-spacer" aria-hidden />
      )}
      <div className="support-msg-time-meta">
        {editedAt ? <span className="support-msg-edited">изм.</span> : null}
        <span>{formatTime(createdAt)}</span>
        {receipt ? <ReceiptTicks status={receipt} /> : null}
      </div>
    </div>
  );
}

function ReceiptTicks({ status }: { status: 'pending' | 'sent' | 'delivered' | 'read' }) {
  const dual = status === 'delivered' || status === 'read';
  const read = status === 'read';
  return (
    <span
      className={`support-receipt${read ? ' is-read' : ''}${status === 'pending' ? ' is-pending' : ''}`}
      title={
        status === 'read'
          ? 'Прочитано'
          : status === 'delivered'
            ? 'Доставлено'
            : status === 'pending'
              ? 'Отправляется…'
              : 'Отправлено'
      }
      aria-label={
        status === 'read'
          ? 'Прочитано'
          : status === 'delivered'
            ? 'Доставлено'
            : status === 'pending'
              ? 'Отправляется'
              : 'Отправлено'
      }
    >
      <svg viewBox="0 0 16 11" width="16" height="11" aria-hidden>
        <path
          d="M1.5 6.2 4.2 9 10.5 1.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {dual ? (
          <path
            d="M5.2 6.2 7.9 9 14.2 1.8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
      </svg>
    </span>
  );
}

type MenuAction = 'edit' | 'delete' | 'forward' | 'select' | 'copy';

function IconChevronDown() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <path
        fill="currentColor"
        d="M7.4 9.4 12 14l4.6-4.6L18 10.8l-6 6-6-6z"
      />
    </svg>
  );
}

export function MessageActionsMenu({
  open,
  x,
  y,
  canEdit,
  canDelete,
  canForward,
  canSelect = false,
  canCopy = false,
  canReact = false,
  onAction,
  onReact,
  onClose,
}: {
  open: boolean;
  x: number;
  y: number;
  canEdit: boolean;
  canDelete: boolean;
  canForward: boolean;
  canSelect?: boolean;
  canCopy?: boolean;
  canReact?: boolean;
  onAction: (action: MenuAction) => void;
  onReact?: (emoji: string) => void;
  onClose: () => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  useEffect(() => {
    if (!open) setPickerOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onScroll = (e: Event) => {
      const t = e.target;
      if (t instanceof Node && menuRef.current?.contains(t)) return;
      onClose();
    };
    window.addEventListener('click', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Place once when menu opens — never re-anchor on picker collapse (avoids jump up).
  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => {
      const el = menuRef.current;
      if (!el) {
        setPos({
          left: Math.min(x, window.innerWidth - 200),
          top: Math.min(y, window.innerHeight - 180),
        });
        return;
      }
      const rect = el.getBoundingClientRect();
      const left = Math.min(Math.max(8, x), window.innerWidth - rect.width - 8);
      const top = Math.min(Math.max(8, y), window.innerHeight - rect.height - 8);
      setPos({ left, top });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, x, y, canReact, canEdit, canDelete, canForward, canSelect, canCopy]);

  // When expanding emoji list, only nudge up if bottom would clip — do not move on close.
  useEffect(() => {
    if (!open || !pickerOpen) return;
    const frame = window.requestAnimationFrame(() => {
      const el = menuRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos((prev) => {
        const overflowBottom = rect.bottom - (window.innerHeight - 8);
        const overflowRight = rect.right - (window.innerWidth - 8);
        if (overflowBottom <= 0 && overflowRight <= 0 && rect.left >= 8) return prev;
        const left =
          overflowRight > 0
            ? Math.max(8, prev.left - overflowRight)
            : Math.max(8, Math.min(prev.left, window.innerWidth - rect.width - 8));
        const top = overflowBottom > 0 ? Math.max(8, prev.top - overflowBottom) : prev.top;
        if (left === prev.left && top === prev.top) return prev;
        return { left, top };
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, pickerOpen]);

  if (!open) return null;

  const pick = (emoji: string) => {
    pushRecent(emoji);
    onReact?.(emoji);
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className={`support-msg-menu${canReact ? ' has-react' : ''}${pickerOpen ? ' is-picker-open' : ''}`}
      style={{ left: pos.left, top: pos.top }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {canReact ? (
        <div className="support-msg-menu-react">
          <div className="support-msg-menu-react-row">
            {REACTION_QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="support-msg-menu-emoji"
                title={emoji}
                onClick={() => pick(emoji)}
              >
                {emoji}
              </button>
            ))}
            <button
              type="button"
              className={`support-msg-menu-expand${pickerOpen ? ' is-open' : ''}`}
              title={pickerOpen ? 'Свернуть' : 'Ещё эмодзи'}
              aria-expanded={pickerOpen}
              onClick={(e) => {
                e.stopPropagation();
                setPickerOpen((v) => !v);
              }}
            >
              <IconChevronDown />
            </button>
          </div>
          <div className={`support-msg-menu-picker${pickerOpen ? ' is-open' : ''}`} aria-hidden={!pickerOpen}>
            <div className="support-msg-menu-picker-inner">
              {EMOJI_CATEGORIES.map((cat) => (
                <section key={cat.id}>
                  <h4>{cat.title}</h4>
                  <div className="support-msg-menu-picker-grid">
                    {cat.emojis.map((emoji) => (
                      <button
                        key={`${cat.id}-${emoji}`}
                        type="button"
                        className="support-msg-menu-emoji"
                        onClick={() => pick(emoji)}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      <div className="support-msg-menu-actions">
        {canCopy && (
          <button type="button" onClick={() => onAction('copy')}>
            Копировать текст
          </button>
        )}
        {canEdit && (
          <button type="button" onClick={() => onAction('edit')}>
            Изменить
          </button>
        )}
        {canForward && (
          <button type="button" onClick={() => onAction('forward')}>
            Переслать
          </button>
        )}
        {canSelect && (
          <button type="button" onClick={() => onAction('select')}>
            Выбрать
          </button>
        )}
        {canDelete && (
          <button type="button" className="is-danger" onClick={() => onAction('delete')}>
            Удалить
          </button>
        )}
      </div>
    </div>
  );
}

/** Stable voice player — no range thrash on play/pause. */
let activeSupportVoice: HTMLAudioElement | null = null;

function claimSupportVoice(el: HTMLAudioElement) {
  if (activeSupportVoice && activeSupportVoice !== el && !activeSupportVoice.paused) {
    activeSupportVoice.pause();
  }
  activeSupportVoice = el;
}

function releaseSupportVoice(el: HTMLAudioElement) {
  if (activeSupportVoice === el) activeSupportVoice = null;
}

export function VoicePlayer({
  src,
  durationMs,
  onReady,
}: {
  src: string;
  durationMs?: number;
  onReady?: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [ratio, setRatio] = useState(0);
  const [displayMs, setDisplayMs] = useState(durationMs || 0);
  const durationRef = useRef(durationMs && durationMs > 0 ? durationMs / 1000 : 0);

  useEffect(() => {
    setPlaying(false);
    setRatio(0);
    setDisplayMs(durationMs || 0);
    durationRef.current = durationMs && durationMs > 0 ? durationMs / 1000 : 0;
  }, [src, durationMs]);

  useEffect(() => {
    return () => {
      const el = audioRef.current;
      if (el) {
        el.pause();
        releaseSupportVoice(el);
      }
    };
  }, []);

  const formatMs = (ms: number) => {
    const s = Math.max(0, Math.round(ms / 1000));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  const seekFromClientX = (clientX: number) => {
    const el = audioRef.current;
    const bar = barRef.current;
    if (!el || !bar) return;
    const rect = bar.getBoundingClientRect();
    const r = Math.min(1, Math.max(0, (clientX - rect.left) / Math.max(1, rect.width)));
    const dur = durationRef.current || el.duration || 0;
    setRatio(r);
    if (dur > 0 && Number.isFinite(dur)) {
      el.currentTime = r * dur;
      setDisplayMs(Math.round(el.currentTime * 1000));
    }
  };

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      claimSupportVoice(el);
      void el.play().catch(() => setPlaying(false));
    } else {
      el.pause();
    }
  };

  return (
    <div className="support-voice-player">
      <audio
        ref={audioRef}
        src={src}
        preload={src.startsWith('blob:') || src.startsWith('data:') ? 'auto' : 'metadata'}
        playsInline
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) {
            durationRef.current = d;
            if (!playing && ratio === 0) setDisplayMs(Math.round(d * 1000));
          }
          onReady?.();
        }}
        onTimeUpdate={(e) => {
          if (dragging.current) return;
          const el = e.currentTarget;
          const dur = durationRef.current || el.duration || 0;
          if (dur > 0) setRatio(el.currentTime / dur);
          setDisplayMs(Math.round(el.currentTime * 1000));
        }}
        onPlay={(e) => {
          claimSupportVoice(e.currentTarget);
          setPlaying(true);
        }}
        onPause={(e) => {
          if (activeSupportVoice === e.currentTarget) releaseSupportVoice(e.currentTarget);
          setPlaying(false);
        }}
        onEnded={(e) => {
          releaseSupportVoice(e.currentTarget);
          setPlaying(false);
          setRatio(0);
          const dur = durationRef.current;
          setDisplayMs(dur > 0 ? Math.round(dur * 1000) : durationMs || 0);
        }}
        onError={() => {
          // Busting query params on blob:/data: URLs breaks mobile preview playback.
          if (src.startsWith('blob:') || src.startsWith('data:')) return;
          const el = audioRef.current;
          if (!el) return;
          const retries = Number(el.dataset.retries || '0');
          if (retries >= 4) return;
          el.dataset.retries = String(retries + 1);
          window.setTimeout(() => {
            const bust = src.includes('?') ? `&_=${Date.now()}` : `?_=${Date.now()}`;
            el.src = `${src}${bust}`;
            el.load();
          }, 250 * (retries + 1));
        }}
      />
      <button
        type="button"
        className="support-voice-play"
        onClick={toggle}
        aria-label={playing ? 'Пауза' : 'Слушать'}
      >
        {playing ? (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M7 5h3.5v14H7Zm6.5 0H17v14h-3.5Z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M8 5.5v13l11-6.5Z" />
          </svg>
        )}
      </button>
      <div className="support-voice-track">
        <div
          ref={barRef}
          className="support-voice-bar"
          role="slider"
          tabIndex={0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(ratio * 100)}
          onPointerDown={(e) => {
            dragging.current = true;
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
            seekFromClientX(e.clientX);
          }}
          onPointerMove={(e) => {
            if (!dragging.current) return;
            seekFromClientX(e.clientX);
          }}
          onPointerUp={() => {
            dragging.current = false;
          }}
          onPointerCancel={() => {
            dragging.current = false;
          }}
        >
          <div className="support-voice-bar-fill" style={{ width: `${ratio * 100}%` }} />
          <div className="support-voice-bar-knob" style={{ left: `${ratio * 100}%` }} />
        </div>
        <span className="support-voice-time">{formatMs(displayMs)}</span>
      </div>
    </div>
  );
}

const MEDIA_BUBBLE_MAX_PX = 300;

/** Telegram-like: long caption widens bubble and centers a narrow image. */
export function isWideMediaCaption(text: string, imageWidthPx?: number): boolean {
  const t = text.trim();
  if (!t) return false;
  if (t.includes('\n')) return true;
  const estCaptionPx = Math.min(MEDIA_BUBBLE_MAX_PX, t.length * 7.2);
  if (imageWidthPx != null && imageWidthPx > 0) {
    return estCaptionPx > imageWidthPx + 10;
  }
  return t.length > 22;
}

export function MediaCaptionStack({
  caption,
  mediaUrl,
  alt,
  align: _align = 'end',
  variant = 'portal',
  kind = 'image',
  onOpen,
  onMediaReady,
}: {
  caption: string;
  mediaUrl: string;
  alt?: string;
  align: 'end' | 'start';
  variant?: 'portal' | 'admin';
  kind?: 'image' | 'video';
  onOpen?: () => void;
  onMediaReady?: () => void;
}) {
  const [imgW, setImgW] = useState(0);
  const prefix = variant === 'admin' ? 'support-admin' : 'support';
  const text = caption.trim();
  const wide = isWideMediaCaption(text, imgW || undefined);
  const stackClass = [`${prefix}-media-stack`, wide ? 'is-wide-caption' : ''].filter(Boolean).join(' ');

  return (
    <div className={stackClass}>
      {kind === 'video' ? (
        <video
          src={mediaUrl}
          controls
          className={`${prefix}-media-video`}
          preload="metadata"
          onLoadedData={(e) => {
            setImgW(e.currentTarget.offsetWidth);
            onMediaReady?.();
          }}
        />
      ) : (
        <button type="button" className={`${prefix}-media-link`} onClick={onOpen}>
          <img
            src={mediaUrl}
            alt={alt || 'Фото'}
            className={`${prefix}-media-image`}
            onLoad={(e) => {
              setImgW(e.currentTarget.offsetWidth);
              onMediaReady?.();
            }}
          />
        </button>
      )}
      {text ? <div className={`${prefix}-bubble-text`}>{text}</div> : null}
    </div>
  );
}

export function MessageReactionsBar({
  reactions,
  onToggle,
  disabled,
}: {
  reactions?: ReactionChip[] | null;
  /** @deprecated unused — ownership comes from server `me` (role-aware). */
  myTelegramId?: number | null;
  onToggle: (emoji: string) => void;
  disabled?: boolean;
}) {
  const items = (reactions || []).map((r) => ({
    ...r,
    // Server `me` is role-aware (user vs support). Do not infer from telegram id alone.
    me: Boolean(r.me),
  }));
  if (!items.length) return null;
  return (
    <div className="support-msg-reactions">
      {items.map((r) => (
        <button
          key={r.emoji}
          type="button"
          className={`support-reaction-chip${r.me ? ' is-mine' : ''}`}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            onToggle(r.emoji);
          }}
          title={r.me ? 'Убрать свою реакцию' : 'Поставить такую же'}
        >
          {r.emoji}
          {r.count > 1 ? ` ${r.count}` : ''}
        </button>
      ))}
    </div>
  );
}

export function CircleLivePreview({
  stream,
  variant = 'inline',
}: {
  stream: MediaStream | null;
  variant?: 'inline' | 'overlay';
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !stream) return;
    el.srcObject = stream;
    void el.play().catch(() => undefined);
    return () => {
      el.srcObject = null;
    };
  }, [stream]);
  if (!stream) return null;
  return (
    <div className={`support-circle-live-preview${variant === 'overlay' ? ' is-overlay' : ''}`} aria-hidden>
      <video ref={videoRef} muted playsInline autoPlay />
    </div>
  );
}

export function VideoNotePlayer({
  src,
  durationMs,
  onReady,
}: {
  src: string;
  durationMs?: number | null;
  onReady?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fsVideoRef = useRef<HTMLVideoElement | null>(null);
  const seekingRef = useRef(false);
  const fsSyncedRef = useRef(false);
  const [fsOpen, setFsOpen] = useState(false);
  const [fsPlaying, setFsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(
    durationMs && durationMs > 0 ? durationMs / 1000 : 0,
  );

  const ringR = 48.6;
  const ringC = 2 * Math.PI * ringR;
  const thumbAngle = progress * 2 * Math.PI - Math.PI / 2;
  const thumbLeft = 50 + Math.cos(thumbAngle) * ringR;
  const thumbTop = 50 + Math.sin(thumbAngle) * ringR;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || fsOpen) return;
    el.muted = true;
    el.pause();
    // Keep poster/first frame only — avoid continuous decode of heavy WebM circles.
  }, [src, fsOpen]);

  useEffect(() => {
    if (!fsOpen) {
      fsSyncedRef.current = false;
      return;
    }
    const el = fsVideoRef.current;
    if (!el) return;
    if (!fsSyncedRef.current) {
      el.currentTime = videoRef.current?.currentTime || 0;
      fsSyncedRef.current = true;
    }
    el.muted = false;
    if (fsPlaying) void el.play().catch(() => undefined);
    else el.pause();
  }, [fsOpen, fsPlaying, src]);

  useEffect(() => {
    if (!fsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const fs = fsVideoRef.current;
        const inline = videoRef.current;
        if (fs && inline) inline.currentTime = fs.currentTime;
        setFsOpen(false);
        setFsPlaying(true);
        if (inline) {
          inline.muted = true;
          inline.pause();
        }
      }
      if (e.key === ' ') {
        e.preventDefault();
        setFsPlaying((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fsOpen]);

  const openFullscreen = () => {
    fsSyncedRef.current = false;
    setFsPlaying(true);
    setFsOpen(true);
    videoRef.current?.pause();
  };

  const closeFullscreen = () => {
    const fs = fsVideoRef.current;
    const inline = videoRef.current;
    if (fs && inline) inline.currentTime = fs.currentTime;
    setFsOpen(false);
    setFsPlaying(true);
    if (inline) {
      inline.muted = true;
      inline.pause();
    }
  };

  const seekFromClient = (svg: SVGSVGElement, clientX: number, clientY: number) => {
    const el = fsVideoRef.current;
    if (!el) return;
    const dur = el.duration || duration;
    if (!dur || !Number.isFinite(dur)) return;
    const rect = svg.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let angle = Math.atan2(clientY - cy, clientX - cx);
    angle = angle + Math.PI / 2;
    if (angle < 0) angle += 2 * Math.PI;
    const ratio = Math.min(1, Math.max(0, angle / (2 * Math.PI)));
    el.currentTime = ratio * dur;
    setProgress(ratio);
  };

  const onRingPointerDown = (e: ReactPointerEvent<SVGCircleElement>) => {
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    e.preventDefault();
    e.stopPropagation();
    seekingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    seekFromClient(svg, e.clientX, e.clientY);
  };

  const onRingPointerMove = (e: ReactPointerEvent<SVGCircleElement>) => {
    if (!seekingRef.current) return;
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    e.preventDefault();
    seekFromClient(svg, e.clientX, e.clientY);
  };

  const onRingPointerUp = (e: ReactPointerEvent<SVGCircleElement>) => {
    if (!seekingRef.current) return;
    seekingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const toggleFsPlay = () => {
    const el = fsVideoRef.current;
    if (fsPlaying) {
      setFsPlaying(false);
      return;
    }
    if (el && (el.ended || el.currentTime >= (el.duration || 0) - 0.05)) {
      el.currentTime = 0;
      setProgress(0);
    }
    setFsPlaying(true);
  };

  const fsBody = () =>
    fsOpen ? (
      <div
        className="support-video-note-fs"
        role="dialog"
        aria-modal="true"
        onClick={closeFullscreen}
      >
        <button
          type="button"
          className="support-lightbox-close"
          aria-label="Закрыть"
          onClick={(e) => {
            e.stopPropagation();
            closeFullscreen();
          }}
        >
          <span aria-hidden>×</span>
        </button>
        <div className="support-video-note-fs-stage" onClick={(e) => e.stopPropagation()}>
          <div className="support-video-note-fs-player">
            <div className="support-video-note-fs-media">
              <video
                ref={fsVideoRef}
                src={src}
                playsInline
                preload="metadata"
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget.duration;
                  if (d && Number.isFinite(d)) setDuration(d);
                }}
                onTimeUpdate={(e) => {
                  if (seekingRef.current) return;
                  const el = e.currentTarget;
                  const d = el.duration || duration;
                  if (d > 0) setProgress(el.currentTime / d);
                }}
                onEnded={() => {
                  setFsPlaying(false);
                  setProgress(1);
                }}
                onClick={toggleFsPlay}
              />
              {!fsPlaying ? (
                <div className="support-video-note-fs-pause" aria-hidden>
                  <span />
                  <span />
                </div>
              ) : null}
            </div>
            <svg
              className="support-video-note-fs-ring"
              viewBox="0 0 100 100"
              aria-label="Перемотка"
            >
              <circle
                className="support-video-note-fs-ring-hit"
                cx="50"
                cy="50"
                r={ringR}
                fill="none"
                onPointerDown={onRingPointerDown}
                onPointerMove={onRingPointerMove}
                onPointerUp={onRingPointerUp}
                onPointerCancel={onRingPointerUp}
              />
              <circle
                className="support-video-note-fs-ring-track"
                cx="50"
                cy="50"
                r={ringR}
                fill="none"
              />
              <circle
                className="support-video-note-fs-ring-value"
                cx="50"
                cy="50"
                r={ringR}
                fill="none"
                strokeDasharray={ringC}
                strokeDashoffset={ringC * (1 - progress)}
                transform="rotate(-90 50 50)"
              />
            </svg>
            <div
              className="support-video-note-fs-thumb"
              style={{ left: `${thumbLeft}%`, top: `${thumbTop}%` }}
              aria-hidden
            />
          </div>
        </div>
      </div>
    ) : null;

  return (
    <>
      <div className="support-video-note">
        <button
          type="button"
          className="support-video-note-hit"
          onClick={(e) => {
            e.stopPropagation();
            openFullscreen();
          }}
          title="Открыть"
        >
          <video
            ref={videoRef}
            src={src}
            muted
            playsInline
            preload="metadata"
            onLoadedData={onReady}
          />
        </button>
        {durationMs && durationMs > 0 ? (
          <span className="support-video-note-duration">{Math.round(durationMs / 1000)}с</span>
        ) : null}
      </div>
      {fsBody()}
    </>
  );
}